import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { 
  ArrowLeft, Video, VideoOff, Play, Pause, RotateCcw,
  Pencil, Circle, ArrowRight, Mic, Square,
  Save, Loader2, MousePointer, Check, X, Gauge, MicOff
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const FilmTechniquePage = () => {
  const navigate = useNavigate();
  
  // Phase: record -> edit -> save
  const [phase, setPhase] = useState('record');
  
  // Recording state
  const [isRecording, setIsRecording] = useState(false);
  const [recordedVideoUrl, setRecordedVideoUrl] = useState(null);
  const [recordedBlob, setRecordedBlob] = useState(null);
  const [cameraStream, setCameraStream] = useState(null);
  const [cameraError, setCameraError] = useState(null);
  const [facingMode, setFacingMode] = useState('environment');
  
  // Playback state
  const videoRef = useRef(null);
  const previewRef = useRef(null);
  const canvasRef = useRef(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  
  // Slow-mo state
  const [slowMoSegments, setSlowMoSegments] = useState([]);
  const [markingSlowMo, setMarkingSlowMo] = useState(null);
  
  // Drawing state
  const [tool, setTool] = useState('select');
  const [color, setColor] = useState('#ef4444');
  const [annotations, setAnnotations] = useState([]);
  const [currentDraw, setCurrentDraw] = useState(null);
  const [isDrawing, setIsDrawing] = useState(false);
  
  // Voice-over state - LIVE COMMENTARY MODE
  const [voiceOverEnabled, setVoiceOverEnabled] = useState(false);
  const [voiceOverStream, setVoiceOverStream] = useState(null);
  const [isRecordingVO, setIsRecordingVO] = useState(false);
  const [voiceOverBlob, setVoiceOverBlob] = useState(null);
  const [voiceOverUrl, setVoiceOverUrl] = useState(null);
  const voRecorderRef = useRef(null);
  const voChunksRef = useRef([]);
  const audioContextRef = useRef(null);
  const analyserRef = useRef(null);
  const [micLevel, setMicLevel] = useState(0);
  
  // Save state
  const [title, setTitle] = useState('');
  const [saving, setSaving] = useState(false);
  
  const colors = ['#ef4444', '#22c55e', '#3b82f6', '#ffffff'];

  // Camera functions
  const startCamera = async () => {
    try {
      setCameraError(null);
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode, width: { ideal: 1280 }, height: { ideal: 720 } },
        audio: true
      });
      setCameraStream(stream);
      if (previewRef.current) previewRef.current.srcObject = stream;
    } catch (err) {
      setCameraError('Camera access denied. Please allow permissions.');
    }
  };

  const stopCamera = () => {
    if (cameraStream) {
      cameraStream.getTracks().forEach(t => t.stop());
      setCameraStream(null);
    }
  };

  useEffect(() => {
    if (phase === 'record') startCamera();
    return () => stopCamera();
  }, [phase, facingMode]);

  const startRecording = () => {
    if (!cameraStream) return;
    const chunks = [];
    const recorder = new MediaRecorder(cameraStream, { mimeType: 'video/webm' });
    recorder.ondataavailable = (e) => e.data.size > 0 && chunks.push(e.data);
    recorder.onstop = () => {
      const blob = new Blob(chunks, { type: 'video/webm' });
      setRecordedBlob(blob);
      setRecordedVideoUrl(URL.createObjectURL(blob));
      stopCamera();
      setPhase('edit');
    };
    voRecorderRef.current = recorder;
    recorder.start();
    setIsRecording(true);
  };

  const stopRecording = () => {
    if (voRecorderRef.current && isRecording) {
      voRecorderRef.current.stop();
      setIsRecording(false);
    }
  };

  // Playback
  const togglePlay = () => {
    if (!videoRef.current) return;
    if (isPlaying) videoRef.current.pause();
    else videoRef.current.play();
    setIsPlaying(!isPlaying);
  };

  const handleTimeUpdate = () => {
    if (!videoRef.current) return;
    const t = videoRef.current.currentTime;
    setCurrentTime(t);
    // Apply slow-mo
    const seg = slowMoSegments.find(s => t >= s.start && t <= s.end);
    videoRef.current.playbackRate = seg ? seg.rate : 1;
  };

  // Slow-mo
  const markSlowMoStart = () => {
    setMarkingSlowMo({ start: currentTime });
    toast.info('Start marked! Play to end point, then tap speed.');
  };

  const markSlowMoEnd = (rate) => {
    if (markingSlowMo && currentTime > markingSlowMo.start) {
      setSlowMoSegments(prev => [...prev, {
        id: Date.now(), start: markingSlowMo.start, end: currentTime, rate
      }]);
      setMarkingSlowMo(null);
      toast.success(`Slow-mo ${rate}x added!`);
    }
  };

  // Drawing
  const getCoords = (e) => {
    const canvas = canvasRef.current;
    if (!canvas) return { x: 0, y: 0 };
    const rect = canvas.getBoundingClientRect();
    const clientX = e.touches ? e.touches[0].clientX : e.clientX;
    const clientY = e.touches ? e.touches[0].clientY : e.clientY;
    return {
      x: ((clientX - rect.left) / rect.width) * canvas.width,
      y: ((clientY - rect.top) / rect.height) * canvas.height
    };
  };

  const handleDrawStart = (e) => {
    if (tool === 'select') return;
    e.preventDefault();
    setIsDrawing(true);
    setCurrentDraw({ id: Date.now(), type: tool, color, timestamp: currentTime, duration: 3, points: [getCoords(e)] });
  };

  const handleDrawMove = (e) => {
    if (!isDrawing || !currentDraw) return;
    e.preventDefault();
    setCurrentDraw(prev => ({ ...prev, points: [...prev.points, getCoords(e)] }));
  };

  const handleDrawEnd = () => {
    if (currentDraw && currentDraw.points.length > 1) {
      setAnnotations(prev => [...prev, currentDraw]);
    }
    setIsDrawing(false);
    setCurrentDraw(null);
  };

  // Canvas rendering
  useEffect(() => {
    const canvas = canvasRef.current;
    const video = videoRef.current;
    if (!canvas || !video) return;
    const ctx = canvas.getContext('2d');
    
    const draw = () => {
      canvas.width = video.videoWidth || 640;
      canvas.height = video.videoHeight || 360;
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      
      const visible = annotations.filter(a => currentTime >= a.timestamp && currentTime <= a.timestamp + a.duration);
      const all = currentDraw ? [...visible, currentDraw] : visible;
      
      all.forEach(a => {
        ctx.strokeStyle = a.color;
        ctx.lineWidth = 4;
        ctx.lineCap = 'round';
        if (a.points.length > 1) {
          ctx.beginPath();
          ctx.moveTo(a.points[0].x, a.points[0].y);
          a.points.forEach(p => ctx.lineTo(p.x, p.y));
          ctx.stroke();
        }
      });
      requestAnimationFrame(draw);
    };
    draw();
  }, [annotations, currentDraw, currentTime]);

  // Voice-over - LIVE COMMENTARY MODE
  // Enable mic for live commentary (records while you work on video)
  const enableVoiceOver = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      setVoiceOverStream(stream);
      setVoiceOverEnabled(true);
      
      // Set up audio level monitoring
      const audioContext = new (window.AudioContext || window.webkitAudioContext)();
      const analyser = audioContext.createAnalyser();
      const source = audioContext.createMediaStreamSource(stream);
      source.connect(analyser);
      analyser.fftSize = 256;
      audioContextRef.current = audioContext;
      analyserRef.current = analyser;
      
      // Monitor mic level
      const checkLevel = () => {
        if (!analyserRef.current) return;
        const dataArray = new Uint8Array(analyserRef.current.frequencyBinCount);
        analyserRef.current.getByteFrequencyData(dataArray);
        const avg = dataArray.reduce((a, b) => a + b, 0) / dataArray.length;
        setMicLevel(avg);
        if (voiceOverEnabled) requestAnimationFrame(checkLevel);
      };
      checkLevel();
      
      toast.success('Microphone enabled! Your voice will record when video plays.');
    } catch (err) {
      toast.error('Microphone access denied');
    }
  };

  const disableVoiceOver = () => {
    if (voiceOverStream) {
      voiceOverStream.getTracks().forEach(t => t.stop());
      setVoiceOverStream(null);
    }
    if (audioContextRef.current) {
      audioContextRef.current.close();
      audioContextRef.current = null;
    }
    setVoiceOverEnabled(false);
    setMicLevel(0);
  };

  // Start recording voice-over when video plays
  const startVoiceOverRecording = () => {
    if (!voiceOverStream || isRecordingVO) return;
    
    voChunksRef.current = [];
    const recorder = new MediaRecorder(voiceOverStream, { mimeType: 'audio/webm' });
    recorder.ondataavailable = (e) => {
      if (e.data.size > 0) voChunksRef.current.push(e.data);
    };
    recorder.onstop = () => {
      if (voChunksRef.current.length > 0) {
        const blob = new Blob(voChunksRef.current, { type: 'audio/webm' });
        setVoiceOverBlob(blob);
        setVoiceOverUrl(URL.createObjectURL(blob));
      }
    };
    voRecorderRef.current = recorder;
    recorder.start(100); // Record in 100ms chunks for better sync
    setIsRecordingVO(true);
  };

  const stopVoiceOverRecording = () => {
    if (voRecorderRef.current && isRecordingVO) {
      voRecorderRef.current.stop();
      setIsRecordingVO(false);
    }
  };

  // Cleanup voice-over on phase change
  useEffect(() => {
    return () => {
      disableVoiceOver();
    };
  }, [phase]);

  // Integrated play/pause that handles voice-over recording
  const togglePlayWithVO = () => {
    if (!videoRef.current) return;
    
    if (isPlaying) {
      videoRef.current.pause();
      setIsPlaying(false);
      // Stop VO recording when paused
      if (voiceOverEnabled && isRecordingVO) {
        stopVoiceOverRecording();
        toast.info('Voice-over paused');
      }
    } else {
      // If VO is enabled but we have existing recording, ask to re-record
      if (voiceOverEnabled && voiceOverUrl && !isRecordingVO) {
        // Clear previous recording for new session
        setVoiceOverBlob(null);
        setVoiceOverUrl(null);
      }
      
      // Start VO recording when video plays (if mic enabled)
      if (voiceOverEnabled) {
        startVoiceOverRecording();
      }
      
      videoRef.current.play();
      setIsPlaying(true);
    }
  };

  // Save
  const handleSave = async () => {
    if (!title.trim()) { toast.error('Enter a title'); return; }
    if (!recordedBlob) { toast.error('No video'); return; }
    
    setSaving(true);
    const token = localStorage.getItem('token');
    
    try {
      const formData = new FormData();
      formData.append('video', recordedBlob, 'technique.webm');
      formData.append('title', title);
      formData.append('description', '');
      formData.append('annotations', JSON.stringify(annotations));
      formData.append('slow_mo_segments', JSON.stringify(slowMoSegments));
      if (voiceOverBlob) formData.append('voice_over', voiceOverBlob, 'voiceover.webm');

      await axios.post(`${API_URL}/api/library/technique-video`, formData, {
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' }
      });
      toast.success('Saved to library!');
      navigate('/library');
    } catch (err) {
      toast.error('Save failed: ' + (err.response?.data?.detail || err.message));
    } finally {
      setSaving(false);
    }
  };

  const formatTime = (s) => `${Math.floor(s/60)}:${String(Math.floor(s%60)).padStart(2,'0')}`;

  // RECORD PHASE
  if (phase === 'record') {
    return (
      <div className="h-screen flex flex-col bg-black">
        <div className="bg-card p-3 flex items-center justify-between">
          <Button variant="ghost" size="icon" onClick={() => navigate('/coach/dashboard')}>
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <span className="font-bold uppercase text-sm">Film Technique</span>
          <Button variant="ghost" size="icon" onClick={() => setFacingMode(f => f === 'user' ? 'environment' : 'user')}>
            <RotateCcw className="w-5 h-5" />
          </Button>
        </div>
        
        <div className="flex-1 relative">
          {cameraError ? (
            <div className="absolute inset-0 flex items-center justify-center text-white text-center p-4">
              <div>
                <VideoOff className="w-12 h-12 mx-auto mb-3 opacity-50" />
                <p className="mb-3">{cameraError}</p>
                <Button onClick={startCamera}>Try Again</Button>
              </div>
            </div>
          ) : (
            <video ref={previewRef} autoPlay playsInline muted className="w-full h-full object-contain" />
          )}
          {isRecording && (
            <div className="absolute top-4 left-4 bg-red-600 text-white px-3 py-1 rounded-full animate-pulse flex items-center gap-2">
              <div className="w-2 h-2 bg-white rounded-full" /> REC
            </div>
          )}
        </div>
        
        <div className="bg-card p-4 flex justify-center">
          <Button
            size="lg"
            onClick={isRecording ? stopRecording : startRecording}
            disabled={!cameraStream}
            className={`h-16 w-16 rounded-full ${isRecording ? 'bg-red-600' : ''}`}
          >
            {isRecording ? <Square className="w-6 h-6 fill-current" /> : <Video className="w-6 h-6" />}
          </Button>
        </div>
      </div>
    );
  }

  // EDIT PHASE
  if (phase === 'edit') {
    return (
      <div className="h-screen flex flex-col bg-background">
        <div className="bg-card border-b p-2 flex items-center justify-between">
          <Button variant="ghost" size="sm" onClick={() => { setPhase('record'); setRecordedVideoUrl(null); }}>
            <RotateCcw className="w-4 h-4 mr-1" /> Re-record
          </Button>
          <Badge>{formatTime(currentTime)} / {formatTime(duration)}</Badge>
          <Button size="sm" onClick={() => setPhase('save')}>
            Next <ArrowRight className="w-4 h-4 ml-1" />
          </Button>
        </div>
        
        {/* Video - takes most space */}
        <div className="flex-1 bg-black relative min-h-0">
          <video
            ref={videoRef}
            src={recordedVideoUrl}
            className="w-full h-full object-contain"
            onTimeUpdate={handleTimeUpdate}
            onLoadedMetadata={() => setDuration(videoRef.current?.duration || 0)}
            onEnded={() => {
              setIsPlaying(false);
              // Auto-stop voice-over recording when video ends
              if (isRecordingVO) {
                stopVoiceOverRecording();
                toast.success('Voice-over recorded!');
              }
            }}
            playsInline
          />
          <canvas
            ref={canvasRef}
            className="absolute inset-0 w-full h-full"
            onMouseDown={handleDrawStart}
            onMouseMove={handleDrawMove}
            onMouseUp={handleDrawEnd}
            onTouchStart={handleDrawStart}
            onTouchMove={handleDrawMove}
            onTouchEnd={handleDrawEnd}
          />
        </div>
        
        {/* Compact controls */}
        <div className="bg-card border-t p-2 space-y-2">
          {/* Timeline */}
          <div className="relative h-6 bg-muted rounded overflow-hidden">
            <div className="absolute inset-y-0 left-0 bg-primary/30" style={{ width: `${(currentTime/duration)*100}%` }} />
            {slowMoSegments.map(s => (
              <div key={s.id} className="absolute inset-y-0 bg-blue-500/50" 
                style={{ left: `${(s.start/duration)*100}%`, width: `${((s.end-s.start)/duration)*100}%` }} />
            ))}
            <input type="range" min="0" max={duration||1} step="0.1" value={currentTime}
              onChange={(e) => { if(videoRef.current) videoRef.current.currentTime = parseFloat(e.target.value); }}
              className="absolute inset-0 w-full opacity-0 cursor-pointer" />
          </div>
          
          {/* Play + Slow-mo row */}
          <div className="flex items-center justify-between gap-2">
            <Button size="sm" variant="outline" onClick={togglePlayWithVO} className="w-12">
              {isPlaying ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4" />}
            </Button>
            
            {!markingSlowMo ? (
              <Button size="sm" variant="outline" onClick={markSlowMoStart} className="flex-1">
                <Gauge className="w-4 h-4 mr-1" /> Mark Slow-Mo
              </Button>
            ) : (
              <div className="flex gap-1 flex-1">
                <Button size="sm" onClick={() => markSlowMoEnd(0.5)} className="flex-1 bg-blue-600 text-xs">0.5x</Button>
                <Button size="sm" onClick={() => markSlowMoEnd(0.25)} className="flex-1 bg-blue-800 text-xs">0.25x</Button>
                <Button size="sm" variant="ghost" onClick={() => setMarkingSlowMo(null)}><X className="w-4 h-4" /></Button>
              </div>
            )}
          </div>
          
          {/* Draw tools row */}
          <div className="flex items-center justify-between">
            <div className="flex gap-1">
              {[['select', MousePointer], ['freehand', Pencil], ['arrow', ArrowRight], ['circle', Circle]].map(([t, Icon]) => (
                <Button key={t} size="sm" variant={tool === t ? 'default' : 'ghost'} onClick={() => setTool(t)} className="h-8 w-8 p-0">
                  <Icon className="w-4 h-4" />
                </Button>
              ))}
            </div>
            <div className="flex gap-1">
              {colors.map(c => (
                <button key={c} onClick={() => setColor(c)}
                  className={`w-6 h-6 rounded-full border-2 ${color === c ? 'border-primary' : 'border-transparent'}`}
                  style={{ backgroundColor: c }} />
              ))}
            </div>
          </div>
          
          {/* Voice-over row - Live Commentary Mode */}
          <div className="flex gap-2 items-center">
            {!voiceOverEnabled ? (
              <Button size="sm" variant="outline" onClick={enableVoiceOver} className="flex-1">
                <Mic className="w-4 h-4 mr-1" /> Enable Mic for Commentary
              </Button>
            ) : (
              <>
                <Button size="sm" variant="destructive" onClick={disableVoiceOver} className="flex-shrink-0">
                  <MicOff className="w-4 h-4" />
                </Button>
                <div className="flex-1 flex items-center gap-2 bg-muted rounded px-2 py-1">
                  <Mic className={`w-4 h-4 ${isRecordingVO ? 'text-red-500 animate-pulse' : 'text-green-500'}`} />
                  <div className="flex-1 h-2 bg-background rounded overflow-hidden">
                    <div 
                      className={`h-full transition-all ${isRecordingVO ? 'bg-red-500' : 'bg-green-500'}`}
                      style={{ width: `${Math.min(100, micLevel * 2)}%` }}
                    />
                  </div>
                  <span className="text-xs text-muted-foreground">
                    {isRecordingVO ? 'Recording...' : 'Ready'}
                  </span>
                </div>
              </>
            )}
            {voiceOverUrl && !isRecordingVO && (
              <Badge variant="secondary" className="flex items-center flex-shrink-0">
                <Check className="w-3 h-3 mr-1" /> Saved
              </Badge>
            )}
          </div>
          
          {/* Helper text */}
          {voiceOverEnabled && !isPlaying && (
            <p className="text-xs text-muted-foreground text-center">
              Press Play to start - your voice will record as the video plays
            </p>
          )}
        </div>
      </div>
    );
  }

  // SAVE PHASE
  return (
    <div className="min-h-screen bg-background p-4">
      <div className="max-w-md mx-auto space-y-4">
        <Button variant="ghost" onClick={() => setPhase('edit')}>
          <ArrowLeft className="w-4 h-4 mr-1" /> Back to Edit
        </Button>
        
        <div className="aspect-video bg-black rounded-lg overflow-hidden">
          <video src={recordedVideoUrl} controls className="w-full h-full" />
        </div>
        
        <div>
          <Label>Title *</Label>
          <Input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="e.g., Proper Heading Technique" />
        </div>
        
        <div className="bg-muted rounded p-3 text-sm space-y-1">
          <p><strong>Summary:</strong></p>
          <p>• Duration: {formatTime(duration)}</p>
          <p>• Slow-mo sections: {slowMoSegments.length}</p>
          <p>• Annotations: {annotations.length}</p>
          <p>• Voice-over: {voiceOverUrl ? 'Yes' : 'No'}</p>
        </div>
        
        <Button onClick={handleSave} disabled={saving || !title.trim()} className="w-full">
          {saving ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Save className="w-4 h-4 mr-1" />}
          {saving ? 'Saving...' : 'Save to Library'}
        </Button>
      </div>
    </div>
  );
};

export default FilmTechniquePage;
